import { useState, useCallback, useMemo } from 'react';

import Input, { InputProps } from '..';

interface InputMoneyProps extends InputProps {
  value: number | string; // Valor recebido no formato decimal
  id: string;
  label?: string;
  placeholder?: string;
  name?: string;
  readonly?: boolean;
  required?: boolean;
  casasDecimais: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const InputMoney = ({
  value,
  id,
  label,
  placeholder,
  name,
  readonly,
  required,
  casasDecimais = 4,
  onChange,
}: InputMoneyProps) => {
  // Converte o valor inicial para string numérica
  const initialNumericValue = useMemo(() => {
    if (value == null) value = ''; // se value for null, transforma em string vazia
    return value.toString().replace(/[^0-9]/g, '');
  }, [value]);

  const [rawValue, setRawValue] = useState<string>(initialNumericValue);

  const formatNumber = useCallback(
    (input: string) => {
      if (!input) return '';

      if (input.length === 1 && casasDecimais > 0) {
        return `0,${input.padStart(casasDecimais, '0')}`;
      }
      // Divide o número em duas partes: inteiros e decimais
      const parteInteira = input.slice(0, -casasDecimais) || '0';
      const parteDecimal = input.slice(-casasDecimais);

      // Formata a parte inteira com separador de milhares
      const inteiroFormatado = new Intl.NumberFormat('pt-BR', {
        useGrouping: true,
      }).format(parseInt(parteInteira, 10));

      // Junta parte inteira e parte decimal
      return `${inteiroFormatado},${parteDecimal.padEnd(casasDecimais, '0')}`;
    },
    [casasDecimais],
  );

  const toFloatValue = useCallback(
    (input: string) => {
      if (!input) return '0';

      const parteInteira = input.slice(0, -casasDecimais) || '0';
      const parteDecimal = input
        .slice(-casasDecimais)
        .padEnd(casasDecimais, '0');

      return `${parteInteira}.${parteDecimal}`;
    },
    [casasDecimais],
  );

  const valorFormatado = useMemo(() => {
    return formatNumber(rawValue);
  }, [rawValue, formatNumber]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Remove tudo que não seja número do valor digitado
    const numericValue = inputValue.replace(/[^0-9]/g, '');

    // Atualiza o valor bruto
    setRawValue(numericValue);

    // Chama o onChange se existir
    if (onChange && name) {
      const eventoInterno = {
        target: {
          name,
          value: toFloatValue(numericValue),
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(eventoInterno);
    }
  };

  return (
    <Input
      id={id}
      value={valorFormatado}
      label={label}
      placeholder={placeholder}
      textAlign="right"
      name={name}
      readonly={readonly}
      required={required}
      onChange={handleOnChange}
    />
  );
};

export default InputMoney;
